
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useSubscriptionManagement = () => {
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  const handleCancelSubscription = async (subscriptionId: string | null): Promise<void> => {
    if (!subscriptionId) {
      toast.error("No active subscription found");
      return;
    }
    
    try {
      // Call the Supabase function to cancel the subscription in Stripe
      // The function now also handles updating the strategy_subscriptions table
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {
          subscriptionId: subscriptionId
        }
      });
      
      if (error) {
        console.error("Error canceling subscription:", error);
        throw new Error(error.message);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      toast.success("Your subscription has been canceled");
      return Promise.resolve();
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to cancel subscription");
      return Promise.reject(error);
    }
  };

  const handleReactivateSubscription = async (subscriptionId: string | null): Promise<void> => {
    if (!subscriptionId) {
      toast.error("No subscription found to reactivate");
      return;
    }
    
    try {
      // Call the Supabase function to reactivate the subscription in Stripe
      // The edge function now also handles updating the strategy_subscriptions table
      const { data, error } = await supabase.functions.invoke('reactivate-subscription', {
        body: {
          subscriptionId: subscriptionId
        }
      });
      
      if (error) {
        console.error("Error reactivating subscription:", error);
        throw new Error(error.message);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      toast.success("Your subscription has been reactivated");
      return Promise.resolve();
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to reactivate subscription");
      return Promise.reject(error);
    }
  };

  const handleUpgradeSubscription = async (subscriptionId: string | null): Promise<void> => {
    if (!subscriptionId) {
      toast.error("No subscription found to upgrade");
      return;
    }
    
    try {
      // Call the Supabase function to upgrade the subscription to yearly in Stripe
      // The edge function now also handles updating the strategy_subscriptions table
      const { data, error } = await supabase.functions.invoke('upgrade-subscription', {
        body: {
          subscriptionId: subscriptionId
        }
      });
      
      if (error) {
        console.error("Error upgrading subscription:", error);
        throw new Error(error.message);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      toast.success("Your subscription has been upgraded to the yearly plan");
      return Promise.resolve();
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to upgrade subscription");
      return Promise.reject(error);
    }
  };

  return {
    isManageDialogOpen,
    setIsManageDialogOpen,
    handleCancelSubscription,
    handleReactivateSubscription,
    handleUpgradeSubscription
  };
};
